import { useContext, useMemo, useState } from "react";
import { LanguageContext } from "../context/LanguageProvider";
import "../css/familymatrix.css";


const ROLE_KEYS = ["FATHER", "MOTHER", "SIBLING_1", "SIBLING_2", "SIBLING_3", "SIBLING_4"];

function getRoleKeyFromQuestion(question, index) {
  const code = (question?.code || "").toUpperCase();

  if (code.includes("FATHER")) return "FATHER";
  if (code.includes("MOTHER")) return "MOTHER";
  if (code.includes("SIBLING_1")) return "SIBLING_1";
  if (code.includes("SIBLING_2")) return "SIBLING_2";
  if (code.includes("SIBLING_3")) return "SIBLING_3";
  if (code.includes("SIBLING_4")) return "SIBLING_4";

  return ROLE_KEYS[index] || `ROLE_${index}`;
}

function getRoleLabel(t, roleKey) {
  switch (roleKey) {
    case "FATHER":
      return t("father");
    case "MOTHER":
      return t("mother");
    case "SIBLING_1":
      return `${t("sibling")} 1`;
    case "SIBLING_2":
      return `${t("sibling")} 2`;
    case "SIBLING_3":
      return `${t("sibling")} 3`;
    case "SIBLING_4":
      return `${t("sibling")} 4`;
    default:
      return roleKey;
  }
}

const isChecked = (q) => parseInt(q?.value ?? 0, 10) === 1;

const FamilyMatrix = ({ group, inputRefs, handleInputBlur, validationErrors, navigateToId }) => {
  const { t } = useContext(LanguageContext);

  const diseaseGroups = group?.subAreaGroupLevel2DtoList || [];
  if (!diseaseGroups.length) return null;

  const errorIds = useMemo(() => {
    const set = new Set();
    (validationErrors || []).forEach((e) => set.add(e.id));
    return set;
  }, [validationErrors]);

  const [openMap, setOpenMap] = useState({});

  const toggleOpen = (rowKey, fallbackAutoOpen) => {
    setOpenMap((prev) => {
      const currentEffective =
        prev[rowKey] !== undefined ? prev[rowKey] : !!fallbackAutoOpen;
      return { ...prev, [rowKey]: !currentEffective };
    });
  };

  return (
    <div className="family-matrix family-matrix--accordion">
      {diseaseGroups.map((dg, idx) => {
        const questions = dg?.subAreaCompositionDtoList || [];

        const rowHasNavigateTo =
  !!navigateToId && questions.some((q) => q?.uniqueId === navigateToId);

        if (!questions.length) return null;

        const diseaseLabel = dg?.name || questions?.[0]?.text || `${t("disease")} ${idx + 1}`;
        const rowKey = String(dg?.productSubAreaId ?? questions?.[0]?.id ?? idx);

        const hasAnyChecked = questions.some((q) => isChecked(q));


        const isOpen =
  openMap[rowKey] !== undefined
    ? openMap[rowKey]
    : (hasAnyChecked || rowHasNavigateTo);

        const roleToQuestion = {};
        questions.forEach((q, qIndex) => {
          roleToQuestion[getRoleKeyFromQuestion(q, qIndex)] = q;
        });

        return (
          <div
         key={rowKey}
  id={rowHasNavigateTo ? `go_to_${navigateToId}` : undefined}
  className={`family-matrix__row ${isOpen ? "family-matrix__row--open family_show" : ""} ${
    rowHasNavigateTo ? "highlight" : ""
  }`} 
       
          >
            <div className="family-matrix__row-header">
              <div
                className="family-matrix__disease-title"
                role="button"
                tabIndex={0}
                onClick={() => toggleOpen(rowKey, hasAnyChecked)}
                onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") toggleOpen(rowKey, hasAnyChecked);
      }}
    >
      {diseaseLabel}
    </div>

    <button
      type="button"
      className="family-matrix__toggle-btn"
      aria-expanded={isOpen}
      aria-controls={`family-matrix-row-${rowKey}`}
      onClick={() => toggleOpen(rowKey, hasAnyChecked)}
    >
      {isOpen ? "−" : "+"}
    </button>
  </div>

  <div
    id={`family-matrix-row-${rowKey}`}
    className={`family-matrix__row-body ${isOpen ? "isOpen" : ""}`}
  >
    <div className="family-matrix__members-grid">
      {ROLE_KEYS.map((roleKey) => {
        const q = roleToQuestion[roleKey];
        if (!q) return null;

        const hasError = errorIds.has(q.id);
        const checked = isChecked(q);

        return (
          <label
 ref={(el) => {
              const refId = q.id;
              inputRefs.current[refId] = el;
 }}

            key={q.id}
            className={`family-matrix__member-cell
              ${checked ? "isChecked" : ""}
              ${hasError ? "hasError" : ""}
              ${q.disabled ? "isDisabled" : ""}`}
          >
            <span className="family-matrix__member-content">
              <input
                type="checkbox"
                className="form-check-input family-matrix__checkbox"
                checked={checked}
                disabled={q.disabled}
                ref={(el) => el && (inputRefs.current[q.uniqueId] = el)}
                onChange={() => {
                  const newValue = checked ? 0 : 1;
                  handleInputBlur({ ...q, value: newValue });
                }}
              />
              <span className="family-matrix__member-label">
                {getRoleLabel(t, roleKey)}
              </span>
            </span>
          </label>
        );
      })}
    </div>
  </div>
</div>

        );
      })}
    </div>
  );
};

export default FamilyMatrix;
